/**
 * Standalone Embed Bundle Entry Point
 *
 * This file is built into a self-contained IIFE bundle that can be loaded
 * directly in a browser without any build process. It renders the embedded
 * form and handles all iframe communication.
 *
 * Usage in HTML:
 * <div id="form-embed-root" data-form-id="123" data-api-base-url="https://api.example.com"></div>
 * <script src="embed-form.js"></script>
 */

// Initialize console override for production
import { initConsoleOverride } from './utils/consoleOverride';
initConsoleOverride();

import React from 'react';
import { createRoot } from 'react-dom/client';
import { EmbedFormRenderer } from './components/embed/EmbedFormRenderer';
import { FormBuilderProvider } from './contexts/FormBuilderContext';
import './styles.css';

/**
 * Initialize the embedded form when DOM is ready
 */
function initializeEmbedForm() {
  console.log('🚀 Initializing embedded form...');

  const rootElement = document.getElementById('form-embed-root');

  if (!rootElement) {
    console.error('❌ Form embed root element not found. Make sure to include <div id="form-embed-root"></div>');
    return;
  }

  console.log('✅ Root element found:', rootElement);

  // Get configuration from data attributes
  const formId = rootElement.getAttribute('data-form-id');
  const apiBaseUrl = rootElement.getAttribute('data-api-base-url');
  const redirectUrl = rootElement.getAttribute('data-redirect-url');
  const webhookUrl = rootElement.getAttribute('data-webhook-url');
  const paystackKey = rootElement.getAttribute('data-paystack-key');

  console.log('📋 Configuration:', { formId, apiBaseUrl, redirectUrl, webhookUrl, paystackKey: paystackKey ? `${paystackKey.substring(0, 10)}...` : 'not set' });

  if (!formId) {
    console.error('❌ Missing data-form-id attribute on #form-embed-root');
    return;
  }

  if (!apiBaseUrl) {
    console.error('❌ Missing data-api-base-url attribute on #form-embed-root');
    return;
  }

  try {
    console.log('🔧 Creating React root...');

    // Remove loading class to hide "Loading form..." text
    rootElement.classList.remove('loading');

    // Create React root and render with FormBuilderProvider
    const root = createRoot(rootElement);

    console.log('🎨 Rendering form with FormBuilderProvider...');

    root.render(
      <React.StrictMode>
        <FormBuilderProvider
          config={{
            base_url: apiBaseUrl,
            api_base_url: apiBaseUrl,
            backend_api_base_url: apiBaseUrl,
            enable_version_history: false,
            enable_sections: true,
            features: {
              payment: paystackKey ? {
                paystackEnabled: true,
                paystackPublicKey: paystackKey,
              } : undefined,
            },
          }}
          applicationId="embedded-form"
        >
          <EmbedFormRenderer
            formId={formId}
            apiBaseUrl={apiBaseUrl}
            redirectUrl={redirectUrl || undefined}
            webhookUrl={webhookUrl || undefined}
          />
        </FormBuilderProvider>
      </React.StrictMode>
    );

    console.log(`✅ Form ${formId} embedded successfully`);
  } catch (error) {
    console.error('❌ Failed to initialize form:', error);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeEmbedForm);
} else {
  // DOM already loaded
  initializeEmbedForm();
}

// Export for potential programmatic usage
export { initializeEmbedForm };
